import { useState } from 'react';
import type { App } from '../services/types';

interface ReviewSectionProps {
  app: App;
}

export default function ReviewSection({ app }: ReviewSectionProps) {
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: ''
  });

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting review:', newReview);
    setShowWriteReview(false);
    setNewReview({ rating: 5, comment: '' });
  };

  const ratingDistribution = [
    { stars: 5, count: Math.floor(app.reviewCount * 0.6) },
    { stars: 4, count: Math.floor(app.reviewCount * 0.25) },
    { stars: 3, count: Math.floor(app.reviewCount * 0.1) },
    { stars: 2, count: Math.floor(app.reviewCount * 0.03) },
    { stars: 1, count: Math.floor(app.reviewCount * 0.02) }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-xl font-semibold text-gray-900">Reviews & Ratings</h3>
        <button
          onClick={() => setShowWriteReview(!showWriteReview)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-edit-line mr-1"></i>
          Write Review
        </button>
      </div>

      {showWriteReview && (
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h4 className="font-semibold text-gray-900 mb-4">Write a Review</h4>
          <form onSubmit={handleSubmitReview} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setNewReview(prev => ({ ...prev, rating: star }))}
                    className={`text-2xl ${star <= newReview.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                  >
                    <i className="ri-star-fill"></i>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Review</label>
              <textarea
                value={newReview.comment}
                onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Share your experience with this app..."
                maxLength={500}
                required
              />
              <p className="text-sm text-gray-500 mt-1">{newReview.comment.length}/500 characters</p>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Submit Review
              </button>
              <button
                type="button"
                onClick={() => setShowWriteReview(false)}
                className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-1">
          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <div className="text-4xl font-bold text-gray-900 mb-2">{app.rating}</div>
            <div className="flex justify-center mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className={`ri-star-fill text-xl ${star <= Math.floor(app.rating) ? 'text-yellow-400' : 'text-gray-300'}`}></i>
              ))}
            </div>
            <div className="text-sm text-gray-600">{app.reviewCount} reviews</div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="space-y-3">
            {ratingDistribution.map((item) => (
              <div key={item.stars} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-16">
                  <span className="text-sm text-gray-600">{item.stars}</span>
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{ width: `${(item.count / app.reviewCount) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-12">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {(app.reviews || []).map((review) => (
          <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">{review.userName}</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className={`ri-star-fill text-sm ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                    ))}
                  </div>
                </div>
                <span className="text-sm text-gray-500">{new Date(review.date).toLocaleDateString()}</span>
              </div>
            </div>
            <p className="text-gray-700 mb-3">{review.comment}</p>
            <div className="flex items-center gap-4">
              <button className="text-sm text-gray-500 hover:text-gray-700 flex items-center whitespace-nowrap">
                <i className="ri-thumb-up-line mr-1"></i>
                Helpful ({review.helpful})
              </button>
              <button className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap">
                <i className="ri-flag-line mr-1"></i>
                Report
              </button>
            </div>
          </div>
        ))}

        {(!app.reviews || app.reviews.length === 0) && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-star-line text-2xl text-gray-400"></i>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">No reviews yet</h4>
            <p className="text-gray-600">Be the first to review this app!</p>
          </div>
        )}
      </div>
    </div>
  );
}
